import React from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import ResponsiveGrid from "../layout/ResponsiveGrid";
import Skeleton from "../ui/Skeleton";

interface PostsSkeletonProps {
  viewMode?: "cards" | "table";
}

const TableRowSkeleton: React.FC = () => (
  <tr className="border-b border-gray-100">
    <td className="px-4 py-3 align-middle min-w-[280px]">
      <div className="flex items-center gap-3">
        <Skeleton
          variant="rectangular"
          width={48}
          height={48}
          className="rounded-lg opacity-30 shrink-0"
        />
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton
            variant="rectangular"
            width="90%"
            height={12}
            className="rounded-full opacity-40"
          />
          <Skeleton
            variant="rectangular"
            width="60%"
            height={12}
            className="rounded-full opacity-30"
          />
        </div>
      </div>
    </td>
    <td className="px-4 py-3 align-middle w-[80px]">
      <Skeleton
        variant="circular"
        width={36}
        height={36}
        className="opacity-30"
      />
    </td>
    <td className="px-4 py-3 align-middle w-[210px]">
      <Skeleton
        variant="rectangular"
        width={150}
        height={36}
        className="rounded-full opacity-30"
      />
    </td>
    <td className="px-4 py-3 align-middle w-[150px]">
      <Skeleton
        variant="rectangular"
        width={70}
        height={12}
        className="rounded-full opacity-20"
      />
    </td>
    <td className="px-4 py-3 align-middle text-right w-[80px]">
      <Skeleton
        variant="circular"
        width={36}
        height={36}
        className="opacity-20 ml-auto"
      />
    </td>
  </tr>
);

export const PostsSkeleton: React.FC<PostsSkeletonProps> = ({
  viewMode = "cards",
}) => {
  const { t } = useTranslation();
  return (
    <div className="w-full animate-fade-in">
      {/* Action Bar - Sincronizado com a barra de filtros real de Posts.tsx */}
      <div className="sticky top-0 z-30 -mt-4 -mx-2 pl-4 pr-2 xl:pl-5 xl:pr-3 py-1.5 bg-white/95 backdrop-blur-md border-b border-purple-200 flex items-center justify-between">
        <div className="flex flex-row flex-wrap gap-3 items-center w-full">
          {/* Busca */}
          <div className="flex-1 min-w-[200px]">
            <Skeleton
              variant="rectangular"
              width="100%"
              height={36}
              className="rounded-xl opacity-30"
            />
          </div>

          {/* 3 Selects de filtro (status, plataforma, autoria) */}
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-[185px] shrink-0">
              <Skeleton
                variant="rectangular"
                width="100%"
                height={36}
                className="rounded-xl opacity-30"
              />
            </div>
          ))}

          {/* Toggle Cards/Lista */}
          <div className="flex items-center gap-1 bg-gray-100/80 p-1 rounded-xl shrink-0 ml-auto">
            <Skeleton
              variant="rectangular"
              width={28}
              height={28}
              className="rounded-lg opacity-30"
            />
            <Skeleton
              variant="rectangular"
              width={28}
              height={28}
              className="rounded-lg opacity-20"
            />
          </div>
        </div>
      </div>

      {viewMode === "table" ? (
        /* Tabela - Refletindo o wrapper e thead reais de Posts.tsx (visão em lista) */
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 mt-2!">
          <table className="w-full text-left border-collapse table-fixed min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-600 uppercase tracking-wider">
                <th className="px-4 py-3">{t("Post")}</th>
                <th className="px-4 py-3 w-[80px]">{t("Rede")}</th>
                <th className="px-4 py-3 w-[210px]">{t("Status")}</th>
                <th className="px-4 py-3 w-[150px]">{t("Engajamento")}</th>
                <th className="px-4 py-3 text-right w-[80px]">{t("Ações")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <TableRowSkeleton key={i} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Posts Grid - Refletindo cols e gap reais de Posts.tsx (!gap-2 !mt-2) */
        <ResponsiveGrid
          cols={{ default: 1, md: 2, lg: 3, xl: 4 }}
          className="gap-2! mt-2!"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            // Div simples (não o componente Card) para copiar exatamente as
            // classes do PostCard real, sem os estilos próprios do Card
            // (rounded-xl, padding e cor de borda diferentes) conflitando.
            <div
              key={i}
              className="border border-purple-200/80 rounded-2xl p-4 bg-white flex flex-col overflow-hidden"
            >
              <div className="flex flex-col h-full">
                {/* Header do Card: "Profile Header" */}
                <div className="flex items-center justify-between gap-3 pb-3 mb-3 border-b border-gray-100 relative z-20">
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    {/* Avatar */}
                    <Skeleton
                      variant="circular"
                      width={36}
                      height={36}
                      className="opacity-30 shrink-0"
                    />

                    {/* Textos (Nome e Status) */}
                    <div className="flex flex-col flex-1 min-w-0 gap-1.5">
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={12}
                        className="rounded-full opacity-30"
                      />
                      <div className="flex items-center gap-1.5">
                        <Skeleton
                          variant="circular"
                          width={6}
                          height={6}
                          className="opacity-20 shrink-0"
                        />
                        <Skeleton
                          variant="rectangular"
                          width={60}
                          height={10}
                          className="rounded-full opacity-20"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Canto direito: ID e Menu */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Skeleton
                      variant="rectangular"
                      width={32}
                      height={18}
                      className="rounded-sm opacity-20 hidden sm:inline-block"
                    />
                    <Skeleton
                      variant="rectangular"
                      width={28}
                      height={28}
                      className="rounded-md opacity-20"
                    />
                  </div>
                </div>

                {/* Conteúdo (texto) - min-h-[64px] igual ao PostCard real (reserva ~3 linhas) */}
                <div className="mb-2 px-1 min-h-[64px] space-y-2">
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={12}
                    className="rounded-full opacity-40"
                  />
                  <Skeleton
                    variant="rectangular"
                    width="95%"
                    height={12}
                    className="rounded-full opacity-40"
                  />
                  <Skeleton
                    variant="rectangular"
                    width="60%"
                    height={12}
                    className="rounded-full opacity-30"
                  />
                </div>

                {/* Hashtags - min-h-[50px] igual ao PostCard real */}
                <div className="mb-2 min-h-[50px] flex flex-wrap items-start gap-1.5">
                  <Skeleton
                    variant="rectangular"
                    width={48}
                    height={18}
                    className="rounded-sm opacity-20"
                  />
                  <Skeleton
                    variant="rectangular"
                    width={64}
                    height={18}
                    className="rounded-sm opacity-20"
                  />
                </div>

                {/* Call to Action - min-h-[48px] igual ao PostCard real */}
                <div className="mb-1.5 min-h-[48px] flex items-start">
                  <Skeleton
                    variant="rectangular"
                    width={90}
                    height={22}
                    className="rounded-md opacity-20"
                  />
                </div>

                {/* Mídia - h-36 (144px) igual ao PostCard real */}
                <div className="mb-2">
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={144}
                    className="rounded-md opacity-10"
                  />
                </div>

                {/* Data + métricas */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                  <Skeleton
                    variant="rectangular"
                    width={110}
                    height={10}
                    className="rounded-full opacity-20"
                  />
                  <Skeleton
                    variant="rectangular"
                    width={40}
                    height={10}
                    className="rounded-full opacity-20 ml-auto"
                  />
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-3">
                  {[0, 1, 2, 3].map((m) => (
                    <Skeleton
                      key={m}
                      variant="rectangular"
                      width={28}
                      height={10}
                      className="rounded-full opacity-15"
                    />
                  ))}
                </div>

                {/* Botões de ação (colados abaixo, sem gap extra) */}
                <div className="flex flex-nowrap items-stretch gap-2 mt-auto">
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={34}
                    className="rounded-xl opacity-30 flex-1"
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={34}
                    className="rounded-xl opacity-20 flex-1"
                  />
                </div>
              </div>
            </div>
          ))}
        </ResponsiveGrid>
      )}

      {/* Paginação */}
      <div className="mt-8 py-6 border-t border-purple-200 flex justify-center items-center gap-2">
        <Skeleton
          variant="rectangular"
          width={36}
          height={36}
          className="rounded-xl opacity-20"
        />
        <div className="flex gap-1 px-4">
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            className="rounded-xl opacity-30"
          />
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            className="rounded-xl opacity-20"
          />
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            className="rounded-xl opacity-10"
          />
        </div>
        <Skeleton
          variant="rectangular"
          width={36}
          height={36}
          className="rounded-xl opacity-20"
        />
      </div>
    </div>
  );
};

export default PostsSkeleton;
